/**
 * IosInstallSheet — guida VISUALE all'installazione su iPhone.
 *
 * Due varianti, scelte automaticamente:
 *   1. Webview in-app (WhatsApp/Instagram/...) o browser iOS non-Safari →
 *      <InAppNotice>: "qui non si può installare, apri in Safari" + copia link.
 *   2. Safari vero su iPhone → <SafariCoachmark>: passi illustrati con le ICONE
 *      REALI di iOS (Condividi, Aggiungi alla schermata Home) e una freccia
 *      animata che punta alla barra in basso dove sta davvero il pulsante.
 *
 * Pensato per utenti NON tecnici: niente gergo, mostra le icone invece di
 * descriverle a parole. Usato sia come overlay (da InstallPrompt) sia inline
 * (in InstallGuideScreen).
 */

import { useState } from 'react';
import { installContext } from '../lib/platform';

const SITE_URL =
  (typeof window !== 'undefined' && window.location && window.location.origin) ||
  'https://nullum.app';

/* ── Icone iOS reali (approssimazioni SF Symbols riconoscibili) ───────────── */

export function ShareGlyph({ size = 22, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
      strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M12 3v12" />
      <path d="M8.5 6.5 12 3l3.5 3.5" />
      <path d="M7.5 10H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1.5" />
    </svg>
  );
}

export function AddHomeGlyph({ size = 22, className = '' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
      strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  );
}

/* ── Variante 1: sei in una webview in-app → apri in Safari ───────────────── */

export function InAppNotice({ appName }) {
  const [copied, setCopied] = useState(false);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(SITE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback: selezione manuale (l'URL è comunque mostrato sotto).
      setCopied(false);
    }
  }

  const host = appName || 'questa app';

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <span className="text-[26px] leading-none">🧭</span>
        <div>
          <p className="text-ink text-glow text-[16px]">
            Aprila in Safari per installarla
          </p>
          <p className="text-[14px] text-ink-dim mt-1 leading-relaxed">
            Stai vedendo Nullum dentro <b className="text-ink">{host}</b>. Da qui
            iPhone <b>non</b> permette di aggiungerla alla home: è una regola di
            Apple. Bastano due tocchi per passare a Safari.
          </p>
        </div>
      </div>

      <ol className="space-y-2.5 text-[14px] leading-relaxed">
        <li className="flex gap-2.5">
          <Num n="1" />
          <span>
            Tocca i <b>tre puntini</b> <span className="px-1.5 border border-ink-dim rounded text-ink">⋯</span>
            {' '}(o l'icona <b>Condividi</b>) in alto a destra dello schermo.
          </span>
        </li>
        <li className="flex gap-2.5">
          <Num n="2" />
          <span>
            Scegli <b className="text-ink">«Apri in Safari»</b> (oppure «Apri nel
            browser»).
          </span>
        </li>
        <li className="flex gap-2.5">
          <Num n="3" />
          <span>In Safari rifai questo passaggio: ti guiderò ad aggiungerla alla home.</span>
        </li>
      </ol>

      <div className="border-t border-ink-dim/40 pt-3 space-y-2">
        <p className="text-[13px] text-ink-dim">
          Non trovi «Apri in Safari»? Copia il link e incollalo nella barra di
          Safari:
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={copyLink}
            className="border border-ink bg-ink text-black font-mono text-[14px] py-2 px-3 tracking-wide"
          >
            {copied ? '✓ Copiato' : '⧉ Copia il link'}
          </button>
          <span className="text-[13px] text-ink-dim select-all break-all">{SITE_URL}</span>
        </div>
      </div>
    </div>
  );
}

/* ── Variante 2: Safari vero → coachmark visuale ──────────────────────────── */

export function SafariCoachmark() {
  return (
    <div className="space-y-5">
      <p className="text-[14px] text-ink-dim leading-relaxed">
        Tre tocchi e Nullum vive sulla tua home come un'app normale — niente
        App Store, niente download.
      </p>

      {/* Passo 1 — il pulsante Condividi, con la sua icona vera */}
      <Stepper n="1">
        <span>
          Tocca il pulsante <b className="text-ink">Condividi</b> nella barra in
          basso. È questa icona:
        </span>
        <span className="inline-flex items-center justify-center w-10 h-10 border border-ink text-ink text-glow rounded-lg ml-1 align-middle">
          <ShareGlyph size={22} />
        </span>
      </Stepper>

      {/* Mini-mappa della toolbar Safari con Condividi evidenziato */}
      <SafariToolbarHint />

      {/* Passo 2 — la voce nel menu, con la sua icona vera */}
      <Stepper n="2">
        <span>
          Scorri il menu e tocca <b className="text-ink">«Aggiungi alla
          schermata Home»</b>:
        </span>
        <span className="mt-2 flex items-center justify-between gap-3 border border-ink-dim rounded-lg px-3 py-2 bg-ink/5">
          <span className="text-ink text-[15px]">Aggiungi alla schermata Home</span>
          <span className="text-ink text-glow"><AddHomeGlyph size={22} /></span>
        </span>
      </Stepper>

      {/* Passo 3 */}
      <Stepper n="3">
        <span>
          Tocca <b className="text-ink">«Aggiungi»</b> in alto a destra. Fatto:
          trovi l'icona di Nullum sulla home.
        </span>
      </Stepper>

      <p className="text-[12.5px] text-ink-dim/90 border-t border-ink-dim/40 pt-3">
        Su iPad in orizzontale il pulsante Condividi è in alto a destra.
      </p>
    </div>
  );
}

/** Rappresentazione della toolbar di Safari (in basso) con Condividi acceso. */
function SafariToolbarHint() {
  return (
    <div className="relative">
      <div className="flex items-center justify-around border border-ink-dim/60 rounded-xl px-3 py-2.5 bg-black/60 text-ink-dim">
        <span className="text-[18px] opacity-40">‹</span>
        <span className="text-[18px] opacity-40">›</span>
        <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg border border-ink text-ink text-glow animate-pulse">
          <ShareGlyph size={20} />
        </span>
        <span className="text-[16px] opacity-40">▢</span>
        <span className="text-[15px] opacity-40">⧉</span>
      </div>
      <p className="text-center text-[12px] text-ink-dim mt-1">
        ↓ così è la barra in basso di Safari — il quadrato acceso è Condividi
      </p>
    </div>
  );
}

function Num({ n }) {
  return (
    <span className="shrink-0 w-6 h-6 border border-ink text-ink text-glow flex items-center justify-center text-[13px] font-mono">
      {n}
    </span>
  );
}

function Stepper({ n, children }) {
  return (
    <div className="flex gap-3 items-start">
      <Num n={n} />
      <div className="flex-1 text-[15px] leading-relaxed">{children}</div>
    </div>
  );
}

/* ── Overlay completo (usato da InstallPrompt) ────────────────────────────── */

export default function IosInstallSheet({ onClose }) {
  const ctx = installContext();
  const needsSafari = ctx.iosNeedsSafari;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md border border-ink bg-black shadow-glow max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <div className="flex items-center justify-between border-b border-ink-dim/50 px-4 py-3 sticky top-0 bg-black">
          <span className="text-ink text-glow text-[15px]">📲 Installa Nullum</span>
          <button
            onClick={onClose}
            aria-label="chiudi"
            className="text-ink-dim hover:text-ink text-[20px] leading-none px-1"
          >
            ✕
          </button>
        </div>
        <div className="p-4">
          {needsSafari ? <InAppNotice appName={ctx.inApp || ctx.otherBrowser} /> : <SafariCoachmark />}
        </div>
      </div>
    </div>
  );
}
